/* eslint-disable react/no-array-index-key */

import classNames from 'classnames';

import type { ApiPriceData } from '../../../../models/ecommerce/product/ApiProduct';
import { Helpers } from '../../../../utility/helpers';
import type { A11yTextType } from '../../../common/components/A11yTextWrapper/A11yTextWrapper';
import type { EcomPromotion } from '../../../common/models/domain/ecommerce/promotion/promotion';
import type { PriceData, PriceType } from '../../../common/models/priceData/priceData';
import {
    getExcludingTaxText,
    getRecycleFeeText,
    ProductInfoProperty,
} from '../../../ecommerce/components/ProductInfoProperty';
import {
    type ProductPresentationLabel,
    type ProductPricePresentationSpec,
    getProductPriceLabelForSpec,
} from '../../../ecommerce/components/ProductPresentationSpecs';
import getProductPrimaryPriceLabel from '../../../ecommerce/components/ProductPrimaryPriceLabel/ProductPrimaryPriceLabel';
import type { CartItemProductListingData } from './CartItemProductListing.types';

type CartItemPricePresentationSpec = ProductPricePresentationSpec | 'primaryPrice' | 'totalPrice';

export type CartPrimaryPriceSpec = 'primaryPrice';
export type CartSecondaryPriceSpec = Extract<
    ProductPricePresentationSpec,
    'historicalPrice' | 'maxPromoUsage' | 'originalPrice'
>;
export type CartTotalPriceSpec = Extract<
    CartItemPricePresentationSpec,
    'totalPrice' | 'recycleFee' | 'excludingTax'
>;

export type CartItemPriceLabelsParamaters = {
    product: CartItemProductListingData;
    cartItem: {
        totalPriceData?: PriceData;
        recycleFeeTotalPriceData?: PriceData;
        id?: string;
        productId: string;
        variantId?: string;
    };
    cartItemPromotion: EcomPromotion | undefined;
    priceType: PriceType;
    a11yTextType: A11yTextType;
    isB2B: boolean;
    isUserMember: boolean;
    arePILPricesEnabled: boolean;
    isPILPricesLogicEnabled: boolean;
};

const getPrimaryPriceLabelForSpec =
    ({
        product,
        a11yTextType,
        priceType,
        isUserMember,
        isPILPricesLogicEnabled,
    }: CartItemPriceLabelsParamaters) =>
    (spec: CartPrimaryPriceSpec): ProductPresentationLabel | null => {
        switch (spec) {
            case 'primaryPrice': {
                return getProductPrimaryPriceLabel({
                    product,
                    priceType,
                    a11yTextType,
                    isUserMember,
                    isPILPricesLogicEnabled,
                });
            }
            default:
                return Helpers.exhaustiveCheck(spec);
        }
    };

const createTotalPriceLabel = (params: {
    totalPriceData: ApiPriceData | undefined;
    priceType: PriceType;
    theme: 'red' | 'black';
    a11yTextType: A11yTextType;
}) => {
    const totalPrice =
        params.totalPriceData && Helpers.getPrice(params.totalPriceData, params.priceType);

    const texts = totalPrice ? Helpers.toEcommerceAriaPriceWithCurrency(totalPrice) : undefined;

    if (!texts?.visual) {
        return null;
    }

    return {
        a11y: texts.a11y,
        render: () => (
            <ProductInfoProperty.Base
                className={classNames('u-textWeightBold', 'u-textLarge')}
                color={params.theme}
                a11yText={texts.a11y}
                a11yType={params.a11yTextType}
            >
                {texts.visual}
            </ProductInfoProperty.Base>
        ),
    };
};

const getTotalPriceLabelForSpec =
    ({ cartItem, cartItemPromotion, priceType, a11yTextType }: CartItemPriceLabelsParamaters) =>
    (spec: CartTotalPriceSpec): ProductPresentationLabel | null => {
        switch (spec) {
            case 'totalPrice': {
                return createTotalPriceLabel({
                    totalPriceData: cartItem.totalPriceData,
                    priceType,
                    theme: cartItemPromotion ? 'red' : 'black',
                    a11yTextType,
                });
            }
            case 'recycleFee': {
                const price =
                    cartItem.recycleFeeTotalPriceData &&
                    Helpers.getPrice(cartItem.recycleFeeTotalPriceData, priceType);
                if (!price) return null;

                const texts = getRecycleFeeText(price);
                if (!texts?.visual) return null;

                return {
                    a11y: texts.a11y,
                    render: () => (
                        <ProductInfoProperty.Base
                            color="black"
                            a11yType={a11yTextType}
                            a11yText={texts.a11y}
                        >
                            {texts.visual}
                        </ProductInfoProperty.Base>
                    ),
                };
            }
            case 'excludingTax': {
                const texts = getExcludingTaxText(priceType);
                if (!texts?.visual) return null;

                return {
                    a11y: texts.a11y,
                    render: () => (
                        <ProductInfoProperty.GrayBoxed
                            a11yText={texts.a11y}
                            a11yType={a11yTextType}
                        >
                            {texts.visual}
                        </ProductInfoProperty.GrayBoxed>
                    ),
                };
            }
            default:
                return Helpers.exhaustiveCheck(spec);
        }
    };

const getSecondaryPriceLabelForSpec =
    ({ priceType, a11yTextType: a11yType, product }: CartItemPriceLabelsParamaters) =>
    (spec: CartSecondaryPriceSpec): ProductPresentationLabel | null => {
        return getProductPriceLabelForSpec(product, priceType, a11yType)(spec);
    };

export const cartItemPriceLabels = {
    createTotalPriceLabel,
    getTotalPriceLabelForSpec,
    getPrimaryPriceLabelForSpec,
    getSecondaryPriceLabelForSpec,
};
